import React, { memo, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useWizard } from '../../../context/WizardContext';
import { CheckIcon } from '../Icons';
import { STEP_STATUS } from '../../../constants/stepStatus';
import StepButton from './StepButton';
import ConnectorLine from './ConnectorLine';

/**
 * StepIndicator
 *
 * Renders a horizontal list of steps, highlighting:
 * - The current step (large ring + shadow)
 * - Completed steps (check icon)
 * - Upcoming steps (muted)
 *
 * Allows clicking on any step the user has visited to jump back.
 *
 * Props:
 * @param {Array<{id: string, label: string}>} steps
 * @param {string}                               currentId
 * @param {string[]}                             completedIds
 * @param {function}                             onStepClick
 * @param {string}                               className
 * @param {string}                               connectorClassName
 * @param {string}                               labelClassName
 */
const StepIndicator = ({
  steps,
  currentId,
  completedIds = [],
  onStepClick,
  className = '',
  connectorClassName = '',
  labelClassName = '',
}) => {
  // Get the set of all visited IDs from context
  const { visitedIds } = useWizard();
  const visitedSet = useMemo(() => new Set(visitedIds), [visitedIds]);

  // Precompute completed set for O(1) lookup
  const completedSet = useMemo(() => new Set(completedIds), [completedIds]);

  // Ref to scroll the current step into view if desired
  const currentRef = useRef(null);

  /**
   * Determine status for a given step:
   * - CURRENT if it's the active one
   * - COMPLETED only if it's in completedSet
   * - UPCOMING otherwise
   */
  const getStatus = (id) => {
    if (id === currentId) return STEP_STATUS.CURRENT;
    if (completedSet.has(id)) return STEP_STATUS.COMPLETED;
    return STEP_STATUS.UPCOMING;
  };

  /**
   * Handle click on a step: only allow if it was visited
   */
  const handleClick = useCallback(
    (id) => {
      if (visitedSet.has(id) && id !== currentId && onStepClick) {
        onStepClick(id);
      }
    },
    [visitedSet, currentId, onStepClick],
  );

  if (!steps?.length) return null;

  return (
    <nav aria-label="Progress steps">
      <ol className={clsx('flex items-start overflow-x-auto gap-4 py-4', className)}>
        {steps.map((step, index) => {
          const status = getStatus(step.id);
          const isCompleted = status === STEP_STATUS.COMPLETED;

          return (
            <li
              key={step.id}
              ref={step.id === currentId ? currentRef : null}
              className="relative flex flex-col items-center flex-none min-w-full sm:flex-1 sm:min-w-[120px]"
            >
              {/* Connector line from previous step */}
              {index > 0 && (
                <ConnectorLine
                  status={getStatus(steps[index - 1].id)}
                  className={connectorClassName}
                />
              )}

              <StepButton
                status={status}
                label={step.label}
                stepNumber={index + 1}
                isCompleted={isCompleted}
                isClickable={visitedSet.has(step.id)} // allow click if visited
                onClick={() => handleClick(step.id)}
                buttonClass="" // you can pass custom via indicatorProps
                labelClass={labelClassName}
              >
                {isCompleted ? <CheckIcon className="w-5 h-5 text-white" /> : index + 1}
              </StepButton>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

StepIndicator.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  currentId: PropTypes.string.isRequired,
  completedIds: PropTypes.arrayOf(PropTypes.string),
  onStepClick: PropTypes.func,
  className: PropTypes.string,
  connectorClassName: PropTypes.string,
  labelClassName: PropTypes.string,
};

export default memo(StepIndicator);
